import { ReactElement } from "react";
import { HubDetailsLayout, MediaContentModel } from "../../HubDetailsLayout";
import {
	ContentViewer,
	ContentViewerEmptyBlockSettings,
	HubItemLayoutContentViewerModel
} from "../../Builder/Viewer/ContentViewer";
import {
	TagList,
	TagModel,
	PipeSeparator
} from "client/Components";
import {
	ContentTitle,
	HubItemNameOnlyCategory,
	isFullCategoryModel,
	resolveTitle,
	resolveTitleKind
} from "../../Content/common";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { ActiveFilter, ImageUrlWithAttribution } from "../../common";
import { LinkedDocumentPreviewModel, LinkedDocumentsPreview } from "../../LinkedDocumentsPreview";
import { HubLinkedDocumentViewModel } from "client/Api/Hub/common";
import { localize } from "client_legacy/ndaw/culture";
import { isNotNil } from "client/Misc/utils";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { LinkedDocumentsViewer } from "../../LinkedDocumentsViewer";
import { never } from "client_legacy/ndaw/errors";
import { TimeZone } from "client/DateAndTime/timezones";
import { LocationType, TicketType } from "client/Api/EventsManagement/eventsManagement";
import {
	AttendeeCounter,
	AttendeePreviewModel,
	EventAttendeeKind,
	EventStatisticsModel
} from "client/Api/Hub/Events/eventViewer";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import {
	EventDetails,
	EventDetailsInteractiveProps,
	EventDetailsInvalidInput
} from "./EventDetails";
import { Attendees } from "../Attendees/Attendees";
import { History } from "../../History";
import { EventActions } from "./EventActions/EventActions";

export type EventContentDataProps = ConditionalProps<
	{
		title: ContentTitle;
		category: HubItemCategoryModel | HubItemNameOnlyCategory | undefined;
		selectedCategoryId?: number | null;
		publicationDate: Date | string;
		createdBy: UserIdentityModel | null;
		media: MediaContentModel;
		layout: HubItemLayoutContentViewerModel;
		tags: readonly TagModel<number>[];
		isEditable: boolean;
		startDate: Date | null;
		endDate: Date | null;
		signUpDeadline: Date | null;
		locationType: LocationType;
		location: string | null;
		externalUrl: string | null | EventDetailsInvalidInput;
		onlineEventUrl: string | null | EventDetailsInvalidInput;
		ticketType: TicketType | null;
		availableSpots: number | null | EventDetailsInvalidInput;
		attendeeCounts: readonly AttendeeCounter[];
		isBeforeSignUpDate: boolean;
		isBeforeEventStartDate: boolean;
		isAttendanceEnabled: boolean;
		lastModifiedUser: UserIdentityModel | null;
		lastModifiedDate: Date;
		previewAttendees: readonly AttendeePreviewModel[];
		emptyBlockSettings?: ContentViewerEmptyBlockSettings;
		areCommentsEnabled: boolean;
	},
	"hasData"
>;

export type EventContentStatisticsProps = ConditionalProps<
	{
		statistics: EventStatisticsModel;
		onOpenViewersDialog?: () => void;
		onOpenReactionsDialog: () => void;
		onStatisticsRetrieved: (statistics: EventStatisticsModel) => void;
	},
	"hasStatistics"
>;

export type EventContentInteractiveProps = ConditionalProps<
	{
		id: number;
		attendeeKind: EventAttendeeKind | null;
		onAttend: (options: AttendOptions) => void;
		onAddToCalendar: () => void;
	},
	"isInteractive"
>;

export type EventContentModeProps = {
	mode: "full";
	activeFilter: ActiveFilter | null;
	onBack: () => void;
	onEdit: () => void;
	onSelectCategory: (categoryId: number) => void;
	onSelectAuthor: (authorId: number, authorName: string) => void;
	onTagClick: (tag: number | null) => void;
} | {
	mode: "details-only";
	onSelectCategory?: (categoryId: number) => void;
};

export type EventContentLinkedDocumentsModeProps =
	| {
		linkedDocumentsMode: "preview";
		linkedDocuments: readonly LinkedDocumentPreviewModel[];
		onChangeIsLoading?: undefined;
	}
	| {
		linkedDocumentsMode: "view";
		linkedDocuments: readonly HubLinkedDocumentViewModel[];
		onChangeIsLoading: (isLoading: boolean) => void;
	};

export type EventContentProps =
	& {
		timezone: TimeZone;
		shouldNotRenderEmptyFooter?: boolean;
		onViewImage?: (image: ImageUrlWithAttribution) => void;
	}
	& EventContentDataProps
	& EventContentStatisticsProps
	& EventContentInteractiveProps
	& EventContentModeProps
	& EventContentLinkedDocumentsModeProps;

export const EventContent = (props: EventContentProps): ReactElement => {
	const {
		hasData,
		id,
		title,
		category,
		selectedCategoryId,
		linkedDocuments,
		linkedDocumentsMode,
		mode,
		createdBy,
		isEditable,
		layout,
		media,
		emptyBlockSettings,
		onChangeIsLoading,
		onSelectCategory,
		publicationDate,
		tags,
		attendeeCounts,
		attendeeKind,
		availableSpots,
		endDate,
		externalUrl,
		isAttendanceEnabled,
		isBeforeEventStartDate,
		isBeforeSignUpDate,
		location,
		locationType,
		onlineEventUrl,
		signUpDeadline,
		startDate,
		ticketType,
		lastModifiedDate,
		lastModifiedUser,
		previewAttendees,
		hasStatistics,
		statistics,
		onOpenViewersDialog,
		timezone,
		shouldNotRenderEmptyFooter = false,
		onViewImage,
		onAttend,
		isInteractive,
		onAddToCalendar,
		areCommentsEnabled,
		onStatisticsRetrieved,
		onOpenReactionsDialog
	} = props;

	const renderAuthor = (author: UserIdentityModel): ReactElement | null => {
		const {
			computedDisplayName
		} = getComputedDisplayName({
			firstName: author.firstName,
			lastName: author.lastName
		});

		return (
			<HubDetailsLayout.SubtitleLink
				trackingId="author"
				onClick={
					mode === "full"
						? () => props.onSelectAuthor(author.userId, computedDisplayName)
						: undefined
				}
				tooltip={computedDisplayName}
			>
				{computedDisplayName}
			</HubDetailsLayout.SubtitleLink>
		);
	};

	const renderDetails = (): ReactElement | null => {
		if (!hasData) {
			return null;
		}

		const interactiveProps: EventDetailsInteractiveProps = isInteractive
			? {
				isInteractive: true,
				attendeeKind,
				isAttendanceEnabled,
				onAddToCalendar,
				onAttend
			}
			: {
				isInteractive: false
			};

		const totalAttendees: number = attendeeCounts.reduce(
			(acc, cur) => acc + cur.count,
			0
		);

		return (
			<EventDetails
				{...interactiveProps}
				startDate={startDate}
				endDate={endDate}
				signUpDeadline={signUpDeadline}
				locationType={locationType}
				location={location}
				externalUrl={externalUrl}
				onlineEventUrl={onlineEventUrl}
				ticketType={ticketType}
				availableSpots={availableSpots}
				isBeforeSignUpDate={isBeforeSignUpDate}
				isBeforeEventStartDate={isBeforeEventStartDate}
				timezone={timezone}
				totalAttendees={totalAttendees}
			/>
		);
	};

	const renderLinkedDocuments = (): ReactElement | null => {
		const count: number = linkedDocuments.length;

		if (count === 0) {
			return null;
		}

		switch (linkedDocumentsMode) {
			case "preview":
				return (
					<HubDetailsLayout.LinkedDocuments count={count}>
						<LinkedDocumentsPreview
							items={linkedDocuments}
						/>
					</HubDetailsLayout.LinkedDocuments>
				);
			case "view":
				return (
					<HubDetailsLayout.LinkedDocuments count={count}>
						<LinkedDocumentsViewer
							items={linkedDocuments}
							onChangeIsLoading={onChangeIsLoading}
						/>
					</HubDetailsLayout.LinkedDocuments>
				);
			default:
				never(linkedDocumentsMode);
		}
	};

	const selectedCategoryName: string | null = isNotNil(selectedCategoryId)
		&& isNotNil(category)
		&& isFullCategoryModel(category)
		&& category.id === selectedCategoryId
			? category.name
			: null;

	return (
		<HubDetailsLayout>
			{mode === "full"
				? (
					<>
						<HubDetailsLayout.BackButton
							onBack={props.onBack}
							activeFilter={props.activeFilter}
							selectedCategoryName={selectedCategoryName ?? localize("_AllEvents")}
						/>
						{hasData && (
							<HubDetailsLayout.Title
								onEdit={isEditable ? props.onEdit : undefined}
								text={resolveTitle(title)}
								kind={resolveTitleKind(title)}
							/>
						)}
					</>
				)
				: hasData && (
					<HubDetailsLayout.SimpleTitle
						text={resolveTitle(title)}
						kind={resolveTitleKind(title)}
					/>
				)
			}
			{hasData && (
				<>
					<HubDetailsLayout.Subtitle>
						{isNotNil(createdBy) && (
							<>
								{renderAuthor(createdBy)}
							</>
						)}

						{category && (
							<>
								{createdBy && <PipeSeparator />}
								<HubDetailsLayout.SubtitleLink
									trackingId="category"
									onClick={
										onSelectCategory && isFullCategoryModel(category)
											? () => onSelectCategory(category.id)
											: undefined
									}
									tooltip={category.name}
								>
									{category.name}
								</HubDetailsLayout.SubtitleLink>
							</>
						)}
					</HubDetailsLayout.Subtitle>

					<HubDetailsLayout.Media
						media={media}
						hubItemKind={HubItemKind.Event}
						onViewImage={onViewImage}
					/>

					{renderDetails()}

					<HubDetailsLayout.Separator />

					<HubDetailsLayout.Content>
						<ContentViewer
							hubItemKind={HubItemKind.Event}
							layout={layout}
							onViewImage={onViewImage}
							emptyBlockSettings={emptyBlockSettings}
						/>

						<History>
							<History.Date
								dateName="_Published"
								date={publicationDate}
								user={createdBy}
								onClickAuthor={
									props.mode === "full"
										? props.onSelectAuthor
										: undefined
								}
								trackingId="user-publisher"
								timezone={timezone}
							/>
							<History.Separator />
							<History.Date
								dateName="_LastUpdated"
								date={lastModifiedDate}
								user={lastModifiedUser}
								onClickAuthor={
									props.mode === "full"
										? props.onSelectAuthor
										: undefined
								}
								trackingId="user-modifier"
								timezone={timezone}
							/>
						</History>

						{isInteractive && previewAttendees.length > 0 && (
							<Attendees
								eventId={id}
								eventName={resolveTitle(title)}
								attendees={previewAttendees}
								attendeeCounts={attendeeCounts}
								locationType={locationType}
								isExportAvailable={isEditable}
							/>
						)}
					</HubDetailsLayout.Content>
					{renderLinkedDocuments()}
					{(!shouldNotRenderEmptyFooter
						|| hasStatistics
						|| tags.length > 0)
						&& (
							<HubDetailsLayout.Footer
								stats={hasStatistics
									? {
										reactions: {
											value: statistics.reactionCounters,
											onClick: statistics.reactionCounters.length > 0
												? onOpenReactionsDialog
												: undefined,
										},
										views: {
											value: statistics.viewsCount,
											onClick: statistics.viewsCount > 0
												? onOpenViewersDialog
												: undefined
										},
									}
									: undefined
								}
								rightSide={(
									<TagList
										trackingId="tag-list"
										tags={tags}
										onTagClick={
											props.mode === "full"
												? props.onTagClick
												: undefined
										}
										alignment="right"
										showMoreTagsColor="secondary"
									/>
								)}
							>
								{isInteractive && hasStatistics && (
									<EventActions
										eventId={id}
										statistics={statistics}
										areCommentsEnabled={areCommentsEnabled}
										onStatisticsRetrieved={onStatisticsRetrieved}
									/>
								)}
							</HubDetailsLayout.Footer>
						)
					}
				</>
			)}
		</HubDetailsLayout>
	);
};
EventContent.displayName = "EventContent";

export type AttendOptions = {
	isAttending: false;
} | {
	isAttending: true;
	kind: EventAttendeeKind;
	wasAttending: boolean;
};